import { useMemo } from 'react';
import { FieldPath, RegisterOptions, UseFormReturn } from 'react-hook-form';

import { useReferenceCandidates } from 'components/ReferenceCandidates/useReferenceCandidates';
import { normalizeReferenceNumber } from 'components/ReferenceCandidates/utils';
import { MultiSelectPillInput } from 'components/input/MultiSelectPillInput';
import { NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

type FormFieldPath = FieldPath<NormalizedLoad>;

type LoadRefPillInputProps<TFieldPath extends FormFieldPath> = {
  name: TFieldPath;
  label: string;
  control: UseFormReturn<NormalizedLoad>['control'];
  originalSuggestionData?: NormalizedLoad;
  required?: boolean;
  rules?: RegisterOptions<NormalizedLoad, TFieldPath>;
  disabled?: boolean;
  description?: string;
  autoSelectFirstOption?: boolean;
  pillDisplayContext?: 'loadBuilding' | 'loadView';
  highlightNewValuesInLoadView?: boolean;
  maxChars?: number;
};

const getStringFromPath = (
  source: Maybe<Record<string, unknown>>,
  path: string
): string => {
  if (!source) return '';

  const nestedValue = path.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[segment];
  }, source);

  return typeof nestedValue === 'string' ? nestedValue : '';
};

const NEUTRAL_PILL_CLASSES =
  'bg-neutral-100 text-neutral-700 border-neutral-200';

export function LoadRefPillInput<TFieldPath extends FormFieldPath>({
  name,
  label,
  control,
  originalSuggestionData,
  required = false,
  rules,
  disabled = false,
  description,
  autoSelectFirstOption = false,
  pillDisplayContext = 'loadBuilding',
  highlightNewValuesInLoadView = false,
  maxChars,
}: LoadRefPillInputProps<TFieldPath>) {
  const referenceCandidates = useReferenceCandidates();
  const showSuggestions = pillDisplayContext !== 'loadView';

  const optionValues = useMemo(() => {
    const uniqueOptions = new Set<string>();

    const addMany = (values: string[] = []) => {
      values.forEach((value) => {
        const trimmed = value.trim();
        if (trimmed) {
          uniqueOptions.add(trimmed);
        }
      });
    };

    const addCandidateString = (
      candidate?: string,
      options?: { stripPoPrefix?: boolean }
    ) => {
      if (!candidate) return;
      const cleanedCandidate = options?.stripPoPrefix
        ? candidate.replace(/^PO\s*/i, '').trim()
        : candidate.trim();
      if (cleanedCandidate && !cleanedCandidate.includes(',')) {
        uniqueOptions.add(cleanedCandidate);
      }
    };

    const addCandidateCollection = (candidates: unknown) => {
      if (!candidates) return;
      const candidateArray = Array.isArray(candidates)
        ? candidates
        : Object.values(candidates as Record<string, string>);

      candidateArray
        .map((value) => String(value).trim())
        .filter((value) => value && !value.includes(','))
        .forEach((value) => uniqueOptions.add(value));
    };

    const suggestionData = originalSuggestionData as Maybe<
      Record<string, unknown>
    >;
    const nestedValue = getStringFromPath(suggestionData, name);
    if (nestedValue) {
      addMany(nestedValue.split(','));
    }

    if (String(name).endsWith('refNumber') && originalSuggestionData) {
      addCandidateString(originalSuggestionData.freightTrackingID);
      addCandidateString(originalSuggestionData.pickup?.refNumber, {
        stripPoPrefix: true,
      });
      addCandidateString(originalSuggestionData.consignee?.refNumber);
      addCandidateString(originalSuggestionData.customer?.refNumber);
      if (originalSuggestionData.poNums) {
        addMany(originalSuggestionData.poNums.split(','));
      }
      addCandidateCollection(
        originalSuggestionData.pickup?.refNumberCandidates
      );
      addCandidateCollection(
        originalSuggestionData.consignee?.refNumberCandidates
      );
      addCandidateCollection(
        originalSuggestionData.customer?.refNumberCandidates
      );
    }

    const fieldOptions = Array.from(uniqueOptions);
    // A field that auto-selects its first option would add a candidate without a click.
    if (!showSuggestions || autoSelectFirstOption) {
      return fieldOptions;
    }

    // Numbers found in the email but not filed come first so the rep sees them while editing.
    const fieldOptionKeys = new Set(fieldOptions.map(normalizeReferenceNumber));
    const candidateNumbers = referenceCandidates
      .map((candidate) => candidate.number)
      .filter(
        (number) => !fieldOptionKeys.has(normalizeReferenceNumber(number))
      );
    return [...candidateNumbers, ...fieldOptions];
  }, [
    autoSelectFirstOption,
    name,
    originalSuggestionData,
    referenceCandidates,
    showSuggestions,
  ]);

  return (
    <MultiSelectPillInput
      name={name}
      label={label}
      control={control}
      required={required}
      rules={rules}
      disabled={disabled}
      description={description}
      optionValues={optionValues}
      autoSelectFirstOption={autoSelectFirstOption}
      maxChars={maxChars}
      showSuggestions={showSuggestions}
      hideAIHint={pillDisplayContext === 'loadView'}
      suggestionsLabel={showSuggestions ? 'Suggestions' : undefined}
      suggestionsTooltip={
        showSuggestions ? 'Click to include AI suggestion' : undefined
      }
      resolveSelectedPillClasses={({ isNewValue }) => {
        if (
          pillDisplayContext === 'loadView' &&
          isNewValue &&
          highlightNewValuesInLoadView
        ) {
          return 'bg-warning-100 text-warning-700 border-warning-200';
        }

        return NEUTRAL_PILL_CLASSES;
      }}
    />
  );
}
